// ** Modules
import * as React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

// ** UI
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';

// ** Local
import {
	OVERLAY_DURATION_S,
	TRANSITION_EASING,
	OVERLAY_Z_INDEX,
	BELOW_DESKTOP_MQ,
	BELOW_TABLET_MQ,
	DESKTOP_UP_MQ,
	MOBILE_TOP_INSET_PX,
	DESKTOP_CONTENT_HORIZONTAL_PX,
	DEFAULT_TITLE
} from './loginOverlay.constants';
import { useOverlayAccessibility } from './useOverlayAccessibility';
import CloseButton from './CloseButton';
import ActionItem from './ActionItem';
import Footer from './Footer';
import type { LoginOverlayAction, LoginOverlayProps } from './loginOverlay.types';

export type {
	LoginOverlayAction,
	LoginOverlayProps
} from './loginOverlay.types';

const DEFAULT_ACTIONS: LoginOverlayAction[] = [
	{
		id: 'member-login',
		label: 'Mitglieder Login',
		description: 'Exklusiver Zugriff auf Ihr Dashboard & Fachdokumente.',
		href: '/login'
	},
	{
		id: 'guest-continue',
		label: 'Als Gast fortfahren',
		description: 'Website entdecken, News & Infos lesen.',
		href: '#'
	}
];

const Divider = ({ mt }: { mt: { xs: number; md: number } }) => (
	<Box
		sx={{
			mt,
			width: '100%',
			borderBottom: '1px solid',
			borderColor: 'divider'
		}}
	/>
);

const LoginOverlay = ({
	open,
	onClose,
	title = DEFAULT_TITLE,
	closeLabel = 'Schließen',
	actions = DEFAULT_ACTIONS,
	infoText = 'Weitere Informationen finden Sie in unseren',
	infoLinkLabel = 'FAQ´s.',
	infoLinkHref = 'https://www.tischler.nrw/faq',
	privacyLabel = 'Datenschutz',
	privacyHref = '/datenschutz',
	imprintLabel = 'Impressum',
	imprintHref = '/impressum',
	copyrightLabel = '© 2026 Tischler NRW'
}: LoginOverlayProps) => {
	const theme = useTheme();
	const isBelowDesktop = useMediaQuery(BELOW_DESKTOP_MQ);
	const isBelowTablet = useMediaQuery(BELOW_TABLET_MQ);
	const shouldReduceMotion = useReducedMotion();
	const panelRef = React.useRef<HTMLDivElement>(null);

	useOverlayAccessibility(open, onClose, panelRef);

	const mobileTitle = title === DEFAULT_TITLE && isBelowTablet ? 'Willkommen bei\nTischler NRW' : title;

	const panelInitial = shouldReduceMotion
		? { opacity: 0 }
		: {
				opacity: 0,
				x: isBelowDesktop ? '0%' : '100%',
				y: isBelowDesktop ? '100%' : '0%'
			};

	const panelAnimate = shouldReduceMotion ? { opacity: 1 } : { opacity: 1, x: '0%', y: '0%' };

	const panelExit = shouldReduceMotion
		? { opacity: 0 }
		: {
				opacity: 0,
				x: isBelowDesktop ? '0%' : '100%',
				y: isBelowDesktop ? '100%' : '0%'
			};

	const transition = {
		duration: shouldReduceMotion ? 0 : OVERLAY_DURATION_S,
		ease: TRANSITION_EASING
	};

	return (
		<AnimatePresence>
			{open ? (
				<>
					{/* ── Backdrop ── */}
					<motion.div
						key="backdrop"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={transition}
						onClick={onClose}
						style={{
							position: 'fixed',
							inset: 0,
							zIndex: OVERLAY_Z_INDEX,
							backgroundColor: alpha(theme.palette.common.black, 0.48),
							backdropFilter: 'blur(12px)',
							WebkitBackdropFilter: 'blur(12px)'
						}}
					/>

					{/* ── Animated panel ── */}
					<motion.div
						key="panel"
						initial={panelInitial}
						animate={panelAnimate}
						exit={panelExit}
						transition={transition}
						style={{
							position: 'fixed',
							top: isBelowDesktop ? MOBILE_TOP_INSET_PX : 0,
							right: 0,
							bottom: 0,
							left: isBelowDesktop ? 0 : 'auto',
							width: isBelowDesktop ? '100%' : 'auto',
							height: isBelowDesktop ? 'auto' : '100%',
							zIndex: OVERLAY_Z_INDEX + 1,
							display: 'flex',
							justifyContent: isBelowDesktop ? 'center' : 'flex-end',
							alignItems: isBelowDesktop ? 'flex-end' : 'stretch'
						}}
					>
						<Box
							ref={panelRef}
							role="dialog"
							aria-modal="true"
							aria-label={title}
							onClick={(event) => event.stopPropagation()}
							sx={{
								position: 'relative',
								width: '100%',
								maxWidth: 'none',
								height: isBelowDesktop ? 'auto' : '100%',
								maxHeight: `calc(100dvh - ${MOBILE_TOP_INSET_PX}px)`,
								overflowY: 'auto',
								backgroundColor: 'common.white',
								color: theme.palette.text.secondary,
								boxShadow: `-24px 0 48px ${alpha(theme.palette.common.black, 0.12)}`,
								borderRadius: '8px 8px 0 0',
								[DESKTOP_UP_MQ]: {
									width: '50vw',
									minWidth: 480,
									maxWidth: 'none',
									maxHeight: 'none',
									height: '100%',
									borderRadius: 0,
									backgroundColor: theme.palette.heroLightBackground.main
								}
							}}
						>
							{/* ── Mobile header: title + close icon ── */}
							<Box
								sx={{
									display: 'flex',
									alignItems: 'flex-start',
									justifyContent: 'space-between',
									px: 6,
									pt: 6,
									pb: 1,
									'@media (min-width:768px) and (max-width:1023.95px)': {
										pt: 12
									},
									[DESKTOP_UP_MQ]: { display: 'none' }
								}}
							>
								<Typography
									component="h2"
									sx={{
										color: 'primary.main',
										fontFamily: '"DIN Next LT Pro"',
										fontSize: { xs: '2rem', sm: '2.25rem' },
										fontWeight: 400,
										lineHeight: { xs: '2.5rem', sm: '2.75rem' },
										letterSpacing: { xs: '-0.64px', sm: '-0.72px' },
										textAlign: 'left',
										whiteSpace: 'pre-line'
									}}
								>
									{mobileTitle}
								</Typography>
								<CloseButton
									variant="mobile"
									onClose={onClose}
									closeLabel={closeLabel}
									shouldReduceMotion={shouldReduceMotion ?? false}
								/>
							</Box>

							{/* ── Desktop close button: label + icon ── */}
							<CloseButton
								variant="desktop"
								onClose={onClose}
								closeLabel={closeLabel}
								shouldReduceMotion={shouldReduceMotion ?? false}
							/>

							<Box
								sx={{
									height: '100%',
									display: 'flex',
									flexDirection: 'column',
									px: 6,
									pt: { xs: 2, sm: 3 },
									pb: { xs: 3, sm: 4 },
									[DESKTOP_UP_MQ]: {
										px: DESKTOP_CONTENT_HORIZONTAL_PX,
										pt: 3.5,
										pb: 3.5
									}
								}}
							>
								<Box
									sx={{
										flex: '0 0 auto',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'flex-start',
										width: '100%',
										pt: { xs: 2, sm: 3 },
										[DESKTOP_UP_MQ]: {
											flex: 1,
											alignItems: 'stretch',
											justifyContent: 'center',
											pt: 0
										}
									}}
								>
									<Typography
										component="h2"
										aria-hidden="true"
										sx={{
											display: 'none',
											[DESKTOP_UP_MQ]: {
												display: 'block',
												color: 'primary.main',
												fontFamily: '"DIN Next LT Pro"',
												fontSize: '2.5rem',
												fontWeight: 400,
												lineHeight: '3rem',
												letterSpacing: '-0.8px',
												textAlign: 'left',
												whiteSpace: 'normal'
											}
										}}
									>
										{title}
									</Typography>

									<Divider mt={{ xs: 0, md: 6 }} />

									<Box
										sx={{
											mt: { xs: 8, sm: 8 },
											display: 'flex',
											flexDirection: 'column',
											gap: { xs: 8, sm: 8 },
											[DESKTOP_UP_MQ]: { mt: 12, gap: 8 }
										}}
									>
										{actions.map((action) => (
											<ActionItem key={action.id} action={action} />
										))}
									</Box>

									<Divider mt={{ xs: 8, md: 12 }} />

									<Box
										sx={{
											mt: { xs: 6, sm: 6 },
											maxWidth: 480,
											[DESKTOP_UP_MQ]: { mt: 6, maxWidth: 'none' }
										}}
									>
										<Typography
											variant="label"
											sx={{
												color: 'text.secondary',
												textAlign: 'center',
												[DESKTOP_UP_MQ]: { textAlign: 'left' }
											}}
										>
											{infoText}
										</Typography>
										<Link
											href={infoLinkHref}
											target="_blank"
											rel="noreferrer"
											aria-label={`${infoLinkLabel} (öffnet neues Fenster)`}
											underline="always"
											sx={{
												typography: 'label',
												display: 'block',
												mt: 0.5,
												color: theme.palette.externalLink.main,
												textAlign: 'center',
												[DESKTOP_UP_MQ]: { textAlign: 'left' }
											}}
										>
											{infoLinkLabel}
										</Link>
									</Box>
								</Box>

								<Footer
									privacyLabel={privacyLabel}
									privacyHref={privacyHref}
									imprintLabel={imprintLabel}
									imprintHref={imprintHref}
									copyrightLabel={copyrightLabel}
								/>
							</Box>
						</Box>
					</motion.div>
				</>
			) : null}
		</AnimatePresence>
	);
};

export default LoginOverlay;
